import { CompanyProfile } from './CompanyProfile';
import { capitalize } from 'radash';

export enum ProfileAssemblyStageType {
    ListSimilarCompanies = 'list-similar-companies',
    Import = 'import',
    Images = 'images',
    Enrichment = 'enrichment',
    Services = 'services',
    Products = 'products',
    Publish = 'publish',
}

export type ProfileAssemblyStageTypeOptions = Exclude<
    ProfileAssemblyStageType,
    ProfileAssemblyStageType.ListSimilarCompanies
>;

export enum ProfileAssemblyStageStatus {
    Initialized = 'initialized',
    Pending = 'pending',
    Partial = 'partial',
    Complete = 'complete',
    Failed = 'failed',
}

type ProfileAssemblyStageAction = {
    name: string;
    createdAt: Date;
    log?: Record<string, unknown>;
    job?: string;
    runtimeMs?: number | null;
};

export type ProfileAssemblyStage = {
    status: ProfileAssemblyStageStatus;
    updatedAt: Date;
    actions: ProfileAssemblyStageAction[];
};

export type ProfileAssemblyStages = Record<ProfileAssemblyStageTypeOptions, ProfileAssemblyStage>;

export type ProfileAssembly = {
    id: string;
    manufacturer: string;
    profile: CompanyProfile;
    isDepManaged: boolean;
    stages: ProfileAssemblyStages;
    lastStage: (ProfileAssemblyStage & { name: string }) | null;
    flagged: boolean;
    notes: string;
    batchName?: string;
};

export type ProfileAssemblyLogUpdate = {
    stage: keyof ProfileAssemblyStages;
    status: ProfileAssemblyStageStatus;
    action: { name: string; job?: string; log?: any; runtimeMs?: number };
    flagged?: boolean;
    batchName?: string;
};

export const getDefaultProfileAssemblyLogStages = (): ProfileAssemblyStages => {
    const now = new Date();

    return {
        [ProfileAssemblyStageType.Import]: getDefaultStageValues(now),
        [ProfileAssemblyStageType.Images]: getDefaultStageValues(now),
        [ProfileAssemblyStageType.Enrichment]: getDefaultStageValues(now),
        [ProfileAssemblyStageType.Services]: getDefaultStageValues(now),
        [ProfileAssemblyStageType.Products]: getDefaultStageValues(now),
        [ProfileAssemblyStageType.Publish]: getDefaultStageValues(now),
    };
};

export const getDefaultPayloadProfileAssemblyLogStages = () => {
    const now = new Date();

    return {
        [ProfileAssemblyStageType.Import]: getDefaultPayloadStageValues(now),
        [ProfileAssemblyStageType.Images]: getDefaultPayloadStageValues(now),
        [ProfileAssemblyStageType.Enrichment]: getDefaultPayloadStageValues(now),
        [ProfileAssemblyStageType.Services]: getDefaultPayloadStageValues(now),
        [ProfileAssemblyStageType.Products]: getDefaultPayloadStageValues(now),
        [ProfileAssemblyStageType.Publish]: getDefaultPayloadStageValues(now),
    };
};

const getDefaultStageValues = (now: Date) => ({
    status: ProfileAssemblyStageStatus.Initialized,
    updatedAt: now,
    actions: [],
});

const getDefaultPayloadStageValues = (now: Date) => ({
    ...getDefaultStageValues(now),
    updatedAt: now.toISOString(),
});

export type QueueableTask =
    | 'addLinkedInUrl'
    | 'addCompanyLogo'
    | 'addBasicInfo'
    | 'addAiEnrichment'
    | 'runFirecrawlWebsite'
    | 'addServicesInfo'
    | 'generateApplicationTags'
    | 'generateCaseStudies'
    | 'generateHeroImage'
    | 'assignIndustryCategories'
    | 'addMissionStatement'
    | 'checkRelevantCompany'
    | 'publishEligibleCompany'
    | 'addLocations'
    | 'addProductSeries';

export type QueueableWorkflow = 'fullProfileImport' | 'enrichProfile';

export enum EnrichProfileStep {
    FIRECRAWL_ROOT = 'firecrawl-root',
    FIRECRAWL_COMPANY_INFO = 'firecrawl-company-info',
    FIRECRAWL_LOCATIONS = 'firecrawl-locations',
    FIRECRAWL_SERVICES = 'firecrawl-services',
    FIRECRAWL_PRODUCT_SERIES = 'firecrawl-product-series',
    FIRECRAWL_PRODUCTS = 'firecrawl-products',

    ADD_MISSION_STATEMENT = 'add-mission-statement',
    ADD_LOCATIONS = 'add-locations',
    ADD_SERVICES_INFO = 'add-services-info',
    ADD_PRODUCT_SERIES = 'add-product-series',
}

export const EnrichProfileStepOptions = Object.values(EnrichProfileStep).map((step) => ({
    value: step,
    label: capitalize(step.replace(/-/g, ' ')),
}));
